<template>
  <a-input-number
    v-model:value="inputValue"
    :class="inputClass"
    v-bind="mergedAttrs"
    @change="handleChange"
    class="tw:text-center"
  />
</template>

<script setup lang="ts">
import { computed, normalizeClass, useAttrs, type HTMLAttributes } from 'vue'

interface Props {
  label?: string
  hint?: string
  modelValue?: number
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  precision?: number
  controls?: boolean
  align?: 'left' | 'center' | 'right'
  borderless?: boolean
  class?: HTMLAttributes['class']
  size?: 'large' | 'middle' | 'small'
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  hint: '',
  modelValue: 0,
  min: -Infinity,
  max: Infinity,
  step: 1,
  disabled: false,
  precision: undefined,
  controls: true,
  align: 'left',
  borderless: false,
  class: 'tw:w-full',
  size: 'middle',
})

defineOptions({
  inheritAttrs: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: number]
  change: [value: number]
}>()

const attrs = useAttrs()

const inputValue = computed({
  get: () => props.modelValue,
  set: (val: number | null) => {
    const value = val ?? props.min
    const clamped = Math.max(props.min, Math.min(props.max, value))
    emit('update:modelValue', clamped)
  },
})

const inputClass = computed(() => {
  return normalizeClass([
    'input-number-custom',
    `input-align-${props.align}`,
    { 'tw:bg-s-d-s-dark-background-opacity-100': props.borderless },
    props.class,
  ])
})

// Merge props với attrs, props có priority cao hơn
const mergedAttrs = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { class: _, ...restAttrs } = attrs
  return {
    ...restAttrs,
    min: props.min,
    max: props.max,
    step: props.step,
    disabled: props.disabled,
    precision: props.precision,
    controls: props.controls,
    size: props.size,
    bordered: !props.borderless,
  }
})

const handleChange = (val: number | null) => {
  const value = val ?? props.min
  const clamped = Math.max(props.min, Math.min(props.max, value))
  emit('change', clamped)
}
</script>
